/* Marcar varias filas y llenarles el mismo campo de una.
 *
 * ── POR QUÉ ESTO EXISTE, Y NO ES UNA COMODIDAD ────────────────────────────
 *
 * Es lo que hace que una columna nueva **se llene alguna vez**. El patrón ya
 * se repitió dos veces: `lona` nació con 679 furgones sin cargar, y la columna
 * de larga distancia va a nacer con 224 choferes sin cargar. De a una son
 * novecientos clicks, y eso no lo hace nadie: la columna se queda vacía, «sin
 * dato» deja de ser un estado y pasa a ser el único, y la función muere sin
 * fallar nunca.
 *
 * ── LA SELECCIÓN ES POR ID Y VIVE AFUERA DE LA LISTA ──────────────────────
 *
 * Ésa es la decisión que sostiene todo lo demás. Marcos va a buscar «RGD»,
 * marcar seis, buscar otra cosa y marcar cuatro más — si al cambiar el filtro
 * se perdiera lo ya marcado, esto no serviría para lo que se pidió. Guardar
 * los IDs y no las filas es lo que permite que la selección sobreviva a que la
 * lista se filtre, se ordene y se vuelva a pedir del servidor.
 *
 * Y por eso hay que **decir cuántos hay marcados que no se están viendo**: una
 * selección invisible es la forma de escribirle a gente que uno no se acuerda
 * de haber elegido.
 */

/* Marcar o desmarcar uno. Devuelve un Set NUEVO — mutar el que está en el
 * estado de React no vuelve a dibujar, y el síntoma sería una tilde que no
 * responde. */
export function alternar(seleccion, id) {
  const s = new Set(seleccion || []);
  const k = String(id);
  if (s.has(k)) s.delete(k);
  else s.add(k);
  return s;
}

/* «Marcar todos» marca LO QUE SE ESTÁ VIENDO, no el padrón entero. Con el
 * filtro puesto, marcar 224 cuando en pantalla hay 12 es exactamente la
 * sorpresa que esto tiene que evitar — y la casilla del encabezado está al
 * lado de esas 12. */
export function marcarVisibles(seleccion, filas, id = (f) => f.dni) {
  const s = new Set(seleccion || []);
  for (const f of filas || []) s.add(String(id(f)));
  return s;
}

export function desmarcarVisibles(seleccion, filas, id = (f) => f.dni) {
  const s = new Set(seleccion || []);
  for (const f of filas || []) s.delete(String(id(f)));
  return s;
}

/* ¿La casilla del encabezado va tildada? Sólo si TODAS las visibles lo están.
 * Con la lista vacía va destildada: una tilde sobre nada se lee como que hay
 * algo marcado. */
export function todasVisiblesMarcadas(seleccion, filas, id = (f) => f.dni) {
  const l = filas || [];
  if (!l.length) return false;
  return l.every((f) => (seleccion || new Set()).has(String(id(f))));
}

/* Cuántos hay marcados, y —lo que importa— cuántos NO se están viendo.
 *
 * El segundo número es el que evita el accidente: con el filtro cambiado, la
 * barra puede decir «43 marcados» mientras en pantalla hay 3. Sin el desglose,
 * apretar «aplicar» escribe cuarenta filas que no están a la vista. */
export function resumen(seleccion, visibles, id = (f) => f.dni) {
  const s = seleccion || new Set();
  const enPantalla = (visibles || []).filter((f) => s.has(String(id(f)))).length;
  return { total: s.size, visibles: enPantalla, ocultos: s.size - enPantalla };
}

/* Las filas marcadas, en el orden en que están en la lista completa. Se
 * resuelve contra `todas` y no contra lo visible, justamente porque la
 * selección sobrevive al filtro. */
export function marcadas(seleccion, todas, id = (f) => f.dni) {
  const s = seleccion || new Set();
  return (todas || []).filter((f) => s.has(String(id(f))));
}

/* LAS QUE YA TIENEN OTRO VALOR, que es lo que hay que avisar antes de escribir.
 *
 * Escribir 43 filas es lo más destructivo que ofrece una pantalla de tabla y no
 * se deshace con Ctrl+Z. Si de las 43 hay 5 que ya dicen otra cosa, eso se
 * muestra ANTES y decide la persona — misma regla que `duplicados` y que el
 * cotejo del alta: lo automático avisa, no pisa en silencio.
 *
 * Vacío no cuenta como «otro valor»: llenar un hueco es justamente para lo que
 * sirve esto. Y el mismo valor tampoco — reescribirlo no cambia nada.
 */
export function aPisar(filas, campo, valor) {
  const vacio = (v) => v === null || v === undefined || v === "";
  return (filas || []).filter(
    (f) => !vacio(f?.[campo]) && f[campo] !== valor,
  );
}

/* Cómo se dice lo que va a pasar, con el número adelante.
 *
 * «¿Aplicar?» no dice nada; «Poner Movimientos a 43 choferes» sí. El número va
 * porque es lo único que distingue apretar sobre 3 filas de apretar sobre 224.
 */
export function frase(cuantos, queDice, singular = "chofer", plural = "choferes") {
  if (!cuantos) return "";
  return `${queDice} a ${cuantos} ${cuantos === 1 ? singular : plural}`;
}
